import type {
  DataGridCellRendererProps,
  DataGridRef,
  DataGridSettings,
  DataGridSettingsMenuItem,
  DropDownResult,
  DynamicFormFieldType,
  JsonDataItem,
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
  RowTemplateType,
} from '@myosh/odin-components';
import { DataGrid, isDropDownNoSelection, Notification, OdinDataSender, useTestTag } from '@myosh/odin-components';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExtendedDynamicFormWorkflowStep } from '../../@types/forms';
import type { RiskMatrixRatingValue } from '../../@types/risk-matrix-field';
import type { EntityReference } from '../../@types/views';
import { isOfflineEnabled } from '../../db/odin-db-util';
import { useFetchLinkedRecordColumnValues } from '../../hooks/use-fetch-linked-record-column-values';
import useGridSettings from '../../hooks/use-grid-settings';
import useRenderDataGrid from '../../hooks/use-render-data-grid';
import useFetchWorkflowSteps from '../../hooks/use-workflow-steps';
import { useAppSelector } from '../../redux/hooks';
import usePrefetchFieldOptions from '../../redux/prefetch/use-prefetch-field-options';
import { useFormSettingsQuery } from '../../redux/services/api';
import { useGetDbRecordsByFormIdQuery } from '../../redux/services/db-api';
import { useGetRecordCountQuery, useGetRecordsQuery } from '../../redux/services/record';
import { dynamicPageState } from '../../redux/slices/dynamic-page-slice';
import { selectedGlobalHierarchies, selectedMatchingOption } from '../../redux/slices/global-hierarchies';
import { clearDataGridReference, setDataGridReference } from '../../services/data-grid.service';
import { getDataGridComboboxFilters } from '../../utils/data-grid-filters';
import { dateFormatBasedOnFilter } from '../../utils/data-transformation-utils';
import { columnConfigurator, getCurrentlyLoadedAndTotalRecords } from '../../utils/dynamic-page-utils';
import { buildGlobalHierarchyFiltersUrl, processFieldFilters } from '../../utils/filter-url-builder-utils';
import { buildSortedFieldsUrlString } from '../../utils/sort-url-builder-utils';
import { forceAssert } from '../../utils/type-guards';
import ErrorLoadingDataNotification from '../common/error-loading-data-notification';
import RiskRatingDataCell from '../data-grid-data-cells/risk-rating-data-cell';
import { DataGridLoader } from './data-grid-loader.component';
import { defaultGridSettings as commonGridSettings } from './default-grid-settings';

export interface DefaultFormGridProps {
  menuItems: Array<DataGridSettingsMenuItem>;
  rowTemplate: RowTemplateType;
  selectedForm: DropDownResult;
  moduleId: number;
  onDataLoadCompleted?: () => void;
  onViewChange?: (hasChanged: boolean) => void;
}

function SingleFormGrid({
  menuItems,
  rowTemplate,
  selectedForm,
  moduleId,
  onDataLoadCompleted,
  onViewChange,
}: DefaultFormGridProps) {
  const [gridOptions, setGridOptions] = useState<OdinDataRetrievalOptions>();

  const gridSubscriber = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const gridRef = useRef<DataGridRef | undefined>(undefined);
  const gridSettingsRef = useRef<DataGridSettings>(undefined);

  const { selectedFilters } = useAppSelector(dynamicPageState);
  const { t } = useTranslation();
  const globalFilters = useAppSelector(selectedGlobalHierarchies);
  const globalFiltersMatchingOption = useAppSelector(selectedMatchingOption);

  const { data: formSettings } = useFormSettingsQuery(Number(selectedForm?.value), {
    skip: selectedForm === undefined || (selectedForm?.value as number) <= 0 || isDropDownNoSelection(selectedForm),
  });

  const { fetchLinkedRecordColumnValues, cancelLinkedRecordColumnValuesPendingRequests } =
    useFetchLinkedRecordColumnValues((selectedForm?.value as number) ?? 0);

  usePrefetchFieldOptions(formSettings);

  const { workflowSteps, isFetchingWorkflowSteps } = useFetchWorkflowSteps(selectedForm?.value as number);

  const defaultGridSettings = useMemo<DataGridSettings>(() => {
    const settings: DataGridSettings = {
      ...commonGridSettings,
      components: {
        ...commonGridSettings.components,
        RISK_RATING_DATA_CELL: {
          CellComponent: (props: DataGridCellRendererProps) => {
            const { column, rowData } = props;

            // Extract risk matrix ID directly from rowData
            const columnEntityReference = forceAssert<EntityReference>(column.customDataProperties);
            const entityId = columnEntityReference?.entityId;
            const fields = rowData?.fields as Record<string | number, RiskMatrixRatingValue> | undefined;
            const fieldData = fields?.[entityId];
            const fieldSpecificRiskMatrixId = fieldData?.riskMatrixId;
            const extendedColumnData = {
              ...column,
              customDataProperties: {
                ...column?.customDataProperties,
                displayRecordListColour: formSettings?.displayRecordListColour,
                riskMatrixId: fieldSpecificRiskMatrixId,
              },
            };
            return <RiskRatingDataCell rowData={rowData} column={extendedColumnData} />;
          },
        },
      },
    };

    return settings;
  }, [formSettings]);

  const {
    settings: gridSettings,
    error: gridSettingsError,
    loading: gridSettingsLoading,
  } = useGridSettings(
    defaultGridSettings,
    columnConfigurator,
    selectedForm && !isDropDownNoSelection(selectedForm) ? (selectedForm.value as number) : undefined,
    selectedFilters.viewConfig && !isDropDownNoSelection(selectedFilters.viewConfig)
      ? (selectedFilters.viewConfig.value as number)
      : undefined,
    moduleId,
    false
  );

  gridSettingsRef.current = gridSettings;

  const {
    data: recordsData,
    isLoading: isRecordsLoading,
    isFetching: isRecordsFetching,
    error: recordsError,
  } = useGetRecordsQuery(
    {
      keyTypeId: true,
      formId: selectedForm?.value as number,
      filters: processFieldFilters(gridOptions?.fieldFilters, dateFormatBasedOnFilter),
      globalHierarchyFilters: buildGlobalHierarchyFiltersUrl(globalFilters, globalFiltersMatchingOption),
      sortDirective: buildSortedFieldsUrlString(gridOptions?.sortedFields, gridRef?.current?.api.data.columns),
      page: gridOptions?.page,
      pageSize: gridOptions?.pageSize,
    },
    {
      skip:
        gridOptions === undefined ||
        gridOptions?.page === undefined ||
        gridOptions?.pageSize === undefined ||
        selectedForm === undefined ||
        (selectedForm?.value as number) <= 0 ||
        isDropDownNoSelection(selectedForm) ||
        selectedFilters.viewConfig === undefined ||
        selectedFilters.records === undefined ||
        selectedFilters.lastEdited === undefined ||
        gridSettingsLoading,
    }
  );

  const isRecordsLoaded: boolean = !isRecordsLoading && !isRecordsFetching;

  const { data: dbRecordsData, isFetching: isDbRecordsFetching } = useGetDbRecordsByFormIdQuery(
    selectedForm?.value as number,
    {
      skip: !isOfflineEnabled || selectedForm === undefined || (selectedForm?.value as number) <= 0,
    }
  );

  const {
    data: recordCount,
    isLoading: isRecordsCountLoading,
    isFetching: isRecordsCountFetching,
  } = useGetRecordCountQuery(
    {
      formId: selectedForm?.value as number,
      filters: processFieldFilters(gridOptions?.fieldFilters, dateFormatBasedOnFilter),
      globalHierarchyFilters: buildGlobalHierarchyFiltersUrl(globalFilters, globalFiltersMatchingOption),
    },
    {
      skip:
        gridOptions === undefined ||
        gridOptions?.page === undefined ||
        gridOptions?.pageSize === undefined ||
        selectedForm === undefined ||
        (selectedForm?.value as number) <= 0 ||
        isDropDownNoSelection(selectedForm) ||
        selectedFilters.viewConfig === undefined ||
        selectedFilters.records === undefined ||
        selectedFilters.lastEdited === undefined ||
        gridSettingsLoading,
    }
  );

  const isRecordsCountLoaded: boolean = !isRecordsCountLoading && !isRecordsCountFetching;

  useEffect(() => {
    return () => {
      clearDataGridReference();
      cancelLinkedRecordColumnValuesPendingRequests();
    };
  }, [cancelLinkedRecordColumnValuesPendingRequests]);

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.api.data.getDataOverwritePageCache();
    }
  }, [globalFilters, globalFiltersMatchingOption]);

  useEffect(() => {
    if (recordsError) {
      gridSubscriber.current?.sendData();
      return;
    }

    if (!recordsData || !isRecordsLoaded || !gridSettingsRef.current || gridSettingsLoading) {
      return;
    }

    const data = recordsData.map((record) => record.flat);

    if (recordsData.length === 0) {
      gridSubscriber.current?.sendData();
      onDataLoadCompleted?.();
      return;
    }

    // Fetch linked records and inject into row data before sending to grid
    fetchLinkedRecordColumnValues(gridSettingsRef.current.columns, data).then((linkedRecordsMap) => {
      const dataWithLinkedRecords = data.map((row) => ({
        ...row,
        _linkedRecordColumnValuesMap: linkedRecordsMap,
      }));

      gridSubscriber.current?.sendData({
        data: dataWithLinkedRecords,
        requestId: gridOptions?.requestId,
      });

      onDataLoadCompleted?.();
    });
  }, [
    recordsData,
    isRecordsLoaded,
    gridOptions,
    gridSettingsLoading,
    recordsError,
    onDataLoadCompleted,
    fetchLinkedRecordColumnValues,
  ]);

  const tagCreator = useTestTag('single-form-grid');

  const recordCountDisplayValue = useMemo<string | undefined>(() => {
    if (isRecordsLoaded && isRecordsCountLoaded && recordsData && !gridSettingsLoading) {
      const currentPageRecordsCount = recordsData.length;

      // Adjust page number if there was an error and we are beyond the first page
      let page = gridOptions?.page ?? 1;
      if (recordsError && page > 1) {
        page -= 1;
      }

      return getCurrentlyLoadedAndTotalRecords(
        currentPageRecordsCount,
        recordCount ?? 0,
        gridOptions?.pageSize ?? 1,
        page
      );
    }
    return undefined;
  }, [
    isRecordsLoaded,
    isRecordsCountLoaded,
    recordsData,
    recordCount,
    gridSettingsLoading,
    gridOptions?.pageSize,
    gridOptions?.page,
    recordsError,
  ]);

  const gridData = useMemo<OdinDataRetrieval | undefined>(() => {
    if (!isFetchingWorkflowSteps && formSettings?.id === selectedForm?.value) {
      return {
        getSubscriber: (subscriber, fieldType?: DynamicFormFieldType) => {
          if (fieldType !== 'COMBOBOX') {
            gridSubscriber.current = subscriber;
          }
        },
        getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
          if (options?.fieldType === 'COMBOBOX') {
            await getDataGridComboboxFilters(
              subscriber,
              options,
              false,
              {
                selectedForm,
                workflowSteps,
              },
              formSettings
            );
          } else {
            setGridOptions(options);
          }
        },
      };
    }
  }, [workflowSteps, isFetchingWorkflowSteps, selectedForm?.value, formSettings, selectedForm]);

  const rowIndicator = useMemo(() => {
    return {
      indicator: (data?: JsonDataItem) => {
        const backgroundColor = workflowSteps?.find(
          (step: ExtendedDynamicFormWorkflowStep) => step.label === data?.status
        )?.backgroundColor;
        return <div className="h-4 w-4 rounded" style={{ backgroundColor: backgroundColor }} />;
      },
    };
  }, [workflowSteps]);

  const onDataGridRefCreated = (dataGridRef: DataGridRef) => {
    gridRef.current = dataGridRef;
    setDataGridReference(dataGridRef, { isRecordsListGrid: true });
  };

  const canRenderDataGrid = !gridSettingsLoading && !!gridSettings?.columns && !!gridData && !!selectedForm?.value;
  const { renderDataGrid } = useRenderDataGrid(canRenderDataGrid);

  const handleViewChange = (hasChanged: boolean) => {
    onViewChange?.(hasChanged);
  };

  const dbRecords = useMemo(() => {
    return isOfflineEnabled && !isDbRecordsFetching && dbRecordsData
      ? dbRecordsData.map((dbRecord) => dbRecord.flat)
      : [];
  }, [dbRecordsData, isDbRecordsFetching]);

  return (
    <div className={`-ml-10 flex h-full flex-col overflow-hidden relative${gridSettingsError ? 'justify-center' : ''}`}>
      {!renderDataGrid && !gridSettingsError && <DataGridLoader />}
      {canRenderDataGrid && (
        <>
          <DataGrid
            key={`${moduleId}_${selectedForm.value as unknown as string}`}
            ref={(gridRef) => {
              if (gridRef) onDataGridRefCreated(gridRef);
            }}
            data={gridData}
            gridSettings={gridSettings}
            rowTemplate={rowTemplate}
            rowIndicator={rowIndicator}
            customSettingsMenuItems={menuItems}
            onViewChange={handleViewChange}
            customData={dbRecords}
          />
          <div className="mb-5 px-10 font-semibold" ref={tagCreator('record-count')}>
            {recordCountDisplayValue}
          </div>
        </>
      )}
      {gridSettingsError && (
        <div className="flex items-center justify-center">
          <Notification
            key="configNotification"
            title={t('error-config-title')}
            description={t('error-config-view-description')}
            type="error"
          />
        </div>
      )}
      <ErrorLoadingDataNotification dataError={recordsError} />
    </div>
  );
}

export default SingleFormGrid;
